import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, RefreshCw, Building2, FileSpreadsheet, Trash2 } from "lucide-react";
import { api, STATUS, rupiah } from "@/lib/api";
import { SlotDialog } from "@/components/SlotDialog";
import { PortGrid } from "@/components/PortGrid";
import { colorMap } from "@/components/PtBadge";
import { CompanyRangeDialog } from "@/components/CompanyRangeDialog";
import { BulkNameDialog } from "@/components/BulkNameDialog";
import { ClearNamesDialog } from "@/components/ClearNamesDialog";
import { useAuth } from "@/context/Auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function Legend() {
  return (
    <div className="flex flex-wrap gap-4 text-xs text-slate-500">
      {["active", "inserted", "registering", "error"].map((k) => (
        <span key={k} data-testid={`legend-${k}`} className="inline-flex items-center gap-1.5"><span className={`h-3 w-3 rounded-full ${STATUS[k].dot}`} />{STATUS[k].label}</span>
      ))}
      <span className="inline-flex items-center gap-1.5"><AlertTriangle className="h-3 w-3 text-amber-600" />SIM berganti</span>
    </div>
  );
}

export default function SlotMap() {
  const [devices, setDevices] = useState([]);
  const [dev, setDev] = useState(null);
  const [slots, setSlots] = useState([]);
  const [low, setLow] = useState(5000);
  const [pick, setPick] = useState(null);
  const [q, setQ] = useState("");
  const [colors, setColors] = useState({});
  const loadColors = () => api.get("/companies").then((r) => setColors(colorMap(r.data)));
  const [rangeOpen, setRangeOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const { can, isAdmin: isMaster } = useAuth();
  const isAdmin = can("slot_edit");

  useEffect(() => {
    api.get("/devices").then((r) => { setDevices(r.data); setDev((d) => d || r.data[0] || null); });
    api.get("/settings").then((r) => setLow(r.data.low_balance ?? 5000));
    loadColors();
  }, []);

  const load = useCallback(() => {
    if (dev) api.get("/slots", { params: { device_id: dev.id } }).then((r) => setSlots(r.data)).catch(() => {});
  }, [dev]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const map = useMemo(() => Object.fromEntries(slots.map((s) => [`${s.port}.${s.slot}`, s])), [slots]);
  const counts = useMemo(() => {
    const c = { active: 0, named: 0 };
    slots.forEach((s) => { if (s.status === "active") c.active++; if (s.name) c.named++; });
    return c;
  }, [slots]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">Peta slot</p>
          <h1 className="font-display text-4xl font-bold">{dev?.name || "Belum ada device"}</h1>
        </div>
        <span className="text-sm font-mono text-slate-500 mb-1.5" data-testid="slot-summary">{counts.active} aktif · {counts.named} bernama · {dev?.max_ports}×{dev?.max_slots}</span>
        {isAdmin && <Button data-testid="bulk-name-open" variant="outline" size="sm" className="ml-auto" onClick={() => setBulkOpen(true)}><FileSpreadsheet className="h-4 w-4 mr-1" />Nama massal (Excel)</Button>}
        {isAdmin && dev && <Button data-testid="company-range-open" variant="outline" size="sm" onClick={() => setRangeOpen(true)}><Building2 className="h-4 w-4 mr-1" />Tandai PT</Button>}
        {isMaster && devices.length > 0 && <Button data-testid="clear-names-open" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setClearOpen(true)}><Trash2 className="h-4 w-4 mr-1" />Hapus nama</Button>}
        <Button data-testid="slot-refresh" variant="outline" size="sm" className={isAdmin ? "" : "ml-auto"} onClick={load}><RefreshCw className="h-4 w-4 mr-1" />Muat ulang</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {devices.map((d) => (
          <button key={d.id} data-testid={`device-tab-${d.name}`} onClick={() => setDev(d)}
            className={`rounded-full px-4 py-1.5 text-sm border transition-colors duration-150 ${dev?.id === d.id ? "bg-emerald-500 text-white border-emerald-500 font-semibold" : "border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
            {d.name}{d.is_demo && " (demo)"}
          </button>
        ))}
        <Input data-testid="slot-search-input" placeholder="Sorot nama / PT / nomor…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-48 bg-white ml-auto" />
      </div>
      <Legend />
      {dev && <PortGrid key={dev.id} dev={dev} slots={slots} low={low} q={q} colors={colors} onOpen={(port, slot) => setPick({ port, slot })} />}
      {bulkOpen && <BulkNameDialog onClose={() => setBulkOpen(false)} onSaved={() => { load(); loadColors(); }} />}
      {clearOpen && <ClearNamesDialog devices={devices} current={dev} onClose={() => setClearOpen(false)} onSaved={() => { load(); loadColors(); }} />}
      {rangeOpen && dev && <CompanyRangeDialog device={dev} onClose={() => setRangeOpen(false)} onSaved={() => { load(); loadColors(); }} />}
      {pick && dev && (
        <SlotDialog device={dev} port={pick.port} slot={pick.slot} data={map[`${pick.port}.${pick.slot}`]} low={low}
          onClose={() => setPick(null)} onSaved={() => { load(); loadColors(); }} />
      )}
      <p className="text-xs text-slate-400">Pulsa {rupiah(low)} ke bawah ditandai kuning. Kotak berlatar hijau muda = SIM yang dipilih di port itu; baru ON jika titiknya hijau (Register OK). Untuk memindah SIM aktif, klik slot lalu "Aktifkan SIM ini". Hanya SIM yang ON yang bisa cek pulsa / kirim & terima SMS.</p>
    </div>
  );
}
